import React, {useEffect, useState} from 'react';
import ConfirmModal from "components/ConfirmModal";
import {Trail, TrailAPIData, TrailUpdateAPIData} from "types/Trail";
import {useMutation, useQuery} from "react-query";
import ngage from "services/ngage";
import {useNotifications} from "context/NotificationContext";
import {invalidate} from "services/query-client";
import {SingleCombobox} from "components/Form/Form/Control";
import {useOperator} from "../../../context/OperatorContext";

// Component ----

type TrailUpdate = {
  id: number,
  data: TrailUpdateAPIData
}

type ReorderTrailProps = {
  trail: Trail | null
  show: boolean
  onReorder?: (trail: Trail, order: number) => any
  onReorderFail?: (trail: Trail, order: number, error: unknown) => any
  onCancel?: () => any
}

function ReorderTrail({trail, show, onReorder, onReorderFail, onCancel}: ReorderTrailProps) {
  const {termFor} = useOperator()
  const {notify} = useNotifications()

  const [order, setOrder] = useState(trail?.order ?? 0);

  useEffect(() => {
    if(!!trail) setOrder(trail.order)
  }, [trail])

  const {data: trailCount} = useQuery(
    ['trails.count'],
    () => ngage.trails.count()
  )

  const {mutateAsync: updateTrail} = useMutation(
    (update: TrailUpdate) => ngage.trails.update(update.id, update.data)
  )

  const handleCancel = () => {
    if (onCancel) onCancel()
  }

  const handleReorderTrail = async () => {
    if(!trail) return

    const position = order + 1

    try {
      await updateTrail({id: trail.id, data: {order}})
      await invalidate('trails.all')

      notify("success", `${termFor('Trail')} Moved`, `${trail.name} has been moved to position ${position}`)

      if(onReorder) onReorder(trail, order)
    } catch (error: any) {
      notify("error", `${termFor('Trail')} Not Moved`, `${trail.name} could not be moved to position ${position}`)

      if (onReorderFail) onReorderFail(trail, order, error)
    }
  }

  const options = Array(trailCount).fill(null).map((value, index) => ({
    id: index,
    name: `Position ${index + 1}`
  }))

  return (
    <ConfirmModal
      show={show}
      title={`Move this ${termFor('Trail')}`}
      body={`Change the order of ${termFor('trails')} by moving this one into a new position`}
      approveLabel="Move"
      onApprove={handleReorderTrail}
      onCancel={handleCancel}
    >
      <div className="w-full">
        <SingleCombobox
          options={options}
          selected={options.find(option => option.id === order)}
          onChange={selected => setOrder(Number(selected?.id))}
        />
      </div>
    </ConfirmModal>
  );
}

export default ReorderTrail;